import { Fragment, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, CheckCircle2, Loader2, ShieldCheck, Check, Store,
  Stethoscope, Syringe, FileSignature, Users, Zap, CalendarRange, Clock,
  ClipboardEdit, Search, FileCheck, PoundSterling, UserCheck, Inbox,
  Network, HeartHandshake, Mail,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import PrivacyLink from "../components/PrivacyLink";
import { usePublicConfig } from "../components/calculators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const COVER_TYPES = [
  "Urgent / Last-Minute",
  "Single-Day Locum",
  "Short-Term Cover",
  "Long-Term Cover",
  "Regular Weekly Cover",
  "Independent Prescriber",
  "Other",
];

const COMPETENCIES = ["Pharmacy First", "Flu Vaccination", "COVID-19 Vaccination", "Independent Prescribing", "Other"];

const SOLUTIONS = [
  { icon: Zap, color: "#EF4444", title: "Last-Minute Locum Cover", coverType: "Urgent / Last-Minute",
    text: "Unexpected pharmacist absence or urgent staffing requirement? NCS welcomes urgent enquiries from pharmacies requiring short-notice pharmacist cover. Submit your vacancy details and our team will review pharmacist availability." },
  { icon: CalendarRange, color: "#10B981", title: "Short-Term Pharmacist Cover", coverType: "Short-Term Cover",
    text: "Flexible cover for annual leave, sickness absence, temporary staffing gaps, seasonal demand, additional pharmacy workload and short-term service requirements." },
  { icon: Clock, color: "#06B6D4", title: "Long-Term Pharmacist Placements", coverType: "Long-Term Cover",
    text: "Support for pharmacies requiring regular locum pharmacists, extended temporary placements, ongoing weekly cover, maternity or extended absence cover, and longer-term workforce arrangements." },
  { icon: FileSignature, color: "#F59E0B", title: "Independent Prescribing Pharmacists", coverType: "Independent Prescriber",
    text: "Access to appropriately qualified independent prescribing pharmacists for suitable roles. Availability and service delivery are subject to professional registration, prescribing scope, clinical competencies and relevant governance arrangements." },
];

const SKILLS = [
  { icon: ShieldCheck, color: "#10B981", title: "GPhC Registered",
    text: "Pharmacists registered with the General Pharmaceutical Council, with registration status verified before placement." },
  { icon: Stethoscope, color: "#06B6D4", title: "Pharmacy First",
    text: "Pharmacists trained to support NHS Pharmacy First services, with relevant competence and service requirements verified for each assignment." },
  { icon: Syringe, color: "#F59E0B", title: "Flu Vaccination",
    text: "Pharmacists with flu vaccination training and appropriate competence for the services they undertake." },
  { icon: Syringe, color: "#EF4444", title: "COVID-19 Vaccination",
    text: "Pharmacists with relevant COVID-19 vaccination training and competence, where required." },
  { icon: FileSignature, color: "#10B981", title: "Independent Prescribing",
    text: "Access to pharmacists holding independent prescribing qualifications for appropriate roles, subject to availability." },
  { icon: Store, color: "#06B6D4", title: "Community Pharmacy Experience",
    text: "Pharmacists familiar with community pharmacy operations, patient consultations, dispensing processes and professional responsibilities." },
];

const STEPS = [
  { icon: ClipboardEdit, title: "Submit Your Vacancy",
    text: "Tell us your pharmacy location, required dates, working hours and pharmacist requirements." },
  { icon: Search, title: "NCS Reviews Your Requirements",
    text: "Our team reviews the vacancy, urgency, required competencies and relevant pharmacy services." },
  { icon: Users, title: "Pharmacist Sourcing",
    text: "We identify suitable available pharmacists from our professional network." },
  { icon: FileCheck, title: "Confirmation",
    text: "We discuss the proposed pharmacist, availability, commercial terms and assignment details with your pharmacy." },
  { icon: Store, title: "Pharmacist Cover",
    text: "Once agreed, the pharmacist attends the assignment and provides services within their professional scope and the pharmacy's governance arrangements." },
];

const FEE_CARDS = [
  { icon: PoundSterling, color: "#10B981", title: "Clear Pricing",
    text: "An agreed sourcing fee with no unexpected recruitment charges." },
  { icon: CalendarRange, color: "#06B6D4", title: "Flexible Requirements",
    text: "Support for urgent, short-term and longer-term pharmacist vacancies." },
  { icon: UserCheck, color: "#F59E0B", title: "Professional Matching",
    text: "Sourcing pharmacists according to the pharmacy's requirements and the professional competencies needed." },
];

const PARTNER_BENEFITS = [
  { icon: Inbox, color: "#10B981", title: "Centralised Vacancy Management",
    text: "Submit pharmacist vacancies through one dedicated contact point." },
  { icon: Network, color: "#06B6D4", title: "Multi-Branch Support",
    text: "Coordinate staffing requirements across multiple pharmacy locations." },
  { icon: Zap, color: "#EF4444", title: "Urgent Cover Requests",
    text: "A clear process for submitting last-minute staffing requirements." },
  { icon: FileCheck, color: "#F59E0B", title: "Agreed Commercial Terms",
    text: "Establish sourcing fees and service terms in advance." },
  { icon: HeartHandshake, color: "#10B981", title: "Ongoing Workforce Relationship",
    text: "Develop a long-term working relationship with NCS for recurring pharmacist staffing needs." },
];

const FREQUENCY = [
  "Occasional",
  "Weekly",
  "Multiple Times Per Week",
  "Regular Multi-Branch Cover",
  "Ongoing Workforce Contract",
];

/* ------------------------------ Vacancy form ------------------------------ */
const VacancyForm = ({ coverType, prefillAdditional }) => {
  const empty = {
    pharmacy_name: "", pharmacy_group: "", postcode: "", address: "",
    contact_name: "", job_title: "", email: "", phone: "",
    cover_type: coverType || COVER_TYPES[0], start_date: "", end_date: "",
    start_time: "", finish_time: "", pharmacists: "", rate: "",
    competencies: [], additional: prefillAdditional || "", preferred: false, website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleComp = (c) => setForm((f) => ({
    ...f, competencies: f.competencies.includes(c) ? f.competencies.filter((x) => x !== c) : [...f.competencies, c],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const details = [
      ...(form.pharmacy_group ? [{ label: "Pharmacy group", value: form.pharmacy_group }] : []),
      { label: "Postcode", value: form.postcode },
      { label: "Pharmacy address", value: form.address },
      { label: "Job title", value: form.job_title },
      { label: "Cover type", value: form.cover_type },
      { label: "Cover dates", value: `${form.start_date}${form.end_date ? ` → ${form.end_date}` : ""}` },
      ...(form.start_time || form.finish_time ? [{ label: "Shift times", value: `${form.start_time || "?"} – ${form.finish_time || "?"}` }] : []),
      ...(form.pharmacists ? [{ label: "Pharmacists required", value: form.pharmacists }] : []),
      ...(form.rate ? [{ label: "Hourly rate offered", value: form.rate }] : []),
      ...(form.competencies.length ? [{ label: "Required competencies", value: form.competencies.join(", ") }] : []),
      { label: "Preferred supplier interest", value: form.preferred ? "Yes" : "No" },
    ];
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.contact_name,
        organisation: form.pharmacy_name,
        org_type: "Community Pharmacy Vacancy",
        email: form.email,
        phone: form.phone,
        requirement: `Community Pharmacy — ${form.cover_type}`,
        start_date: form.start_date || null,
        additional: form.additional || null,
        journey: "community-pharmacy",
        website: form.website,
        calculator: details,
      });
      setSent(true);
      toast.success("Vacancy submitted — our community pharmacy team will review your requirements.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="cp-vacancy-success" className="bg-white border border-[#10B981]/30 rounded-3xl p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Vacancy received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.contact_name.split(" ")[0]}. Your vacancy for {form.pharmacy_name} is with
          our community pharmacy team — we will review pharmacist availability and respond shortly.
          A confirmation email is on its way to {form.email}.
        </p>
        <button data-testid="cp-vacancy-new-button" onClick={() => { setSent(false); setForm({ ...empty, cover_type: COVER_TYPES[0], additional: "" }); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Submit another vacancy
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="cp-vacancy-form"
      className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Pharmacy details</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cp-name" className={labelCls}>Pharmacy name *</label>
          <input id="cp-name" data-testid="cp-input-pharmacy-name" required minLength={2}
            value={form.pharmacy_name} onChange={upd("pharmacy_name")} placeholder="Riverside Pharmacy" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-group" className={labelCls}>Pharmacy group <span className="normal-case font-medium text-[#94A3B8]">(if applicable)</span></label>
          <input id="cp-group" data-testid="cp-input-pharmacy-group"
            value={form.pharmacy_group} onChange={upd("pharmacy_group")} placeholder="e.g. NorthWest Pharmacies Ltd" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-postcode" className={labelCls}>Pharmacy postcode *</label>
          <input id="cp-postcode" data-testid="cp-input-postcode" required minLength={4}
            value={form.postcode} onChange={upd("postcode")} placeholder="WN1 1AA" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-address" className={labelCls}>Full pharmacy address *</label>
          <input id="cp-address" data-testid="cp-input-address" required minLength={5}
            value={form.address} onChange={upd("address")} placeholder="12 High Street, Wigan" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-contact" className={labelCls}>Contact name *</label>
          <input id="cp-contact" data-testid="cp-input-contact-name" required minLength={2}
            value={form.contact_name} onChange={upd("contact_name")} placeholder="Amina Shah" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-job" className={labelCls}>Job title *</label>
          <input id="cp-job" data-testid="cp-input-job-title" required minLength={2}
            value={form.job_title} onChange={upd("job_title")} placeholder="Superintendent Pharmacist" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-email" className={labelCls}>Email address *</label>
          <input id="cp-email" data-testid="cp-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="a.shah@pharmacy.co.uk" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-phone" className={labelCls}>Contact telephone *</label>
          <input id="cp-phone" data-testid="cp-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={upd("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
      </div>

      <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Vacancy details</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cp-cover" className={labelCls}>Type of cover required *</label>
          <select id="cp-cover" data-testid="cp-select-cover-type" value={form.cover_type} onChange={upd("cover_type")} className={inputCls}>
            {COVER_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="cp-pharmacists" className={labelCls}>Number of pharmacists required</label>
          <input id="cp-pharmacists" data-testid="cp-input-pharmacists" type="number" min="1"
            value={form.pharmacists} onChange={upd("pharmacists")} placeholder="1" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-start" className={labelCls}>Start date *</label>
          <input id="cp-start" data-testid="cp-input-start-date" type="date" required
            value={form.start_date} onChange={upd("start_date")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-end" className={labelCls}>End date</label>
          <input id="cp-end" data-testid="cp-input-end-date" type="date"
            value={form.end_date} onChange={upd("end_date")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-start-time" className={labelCls}>Start time</label>
          <input id="cp-start-time" data-testid="cp-input-start-time" type="time"
            value={form.start_time} onChange={upd("start_time")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-finish-time" className={labelCls}>Finish time</label>
          <input id="cp-finish-time" data-testid="cp-input-finish-time" type="time"
            value={form.finish_time} onChange={upd("finish_time")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-rate" className={labelCls}>Hourly rate offered</label>
          <input id="cp-rate" data-testid="cp-input-rate"
            value={form.rate} onChange={upd("rate")} placeholder="e.g. £32 per hour" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <span className={labelCls}>Required services or qualifications</span>
          <div className="flex flex-wrap gap-2.5">
            {COMPETENCIES.map((c) => {
              const active = form.competencies.includes(c);
              const key = c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <button type="button" key={c} data-testid={`cp-comp-${key}`}
                  onClick={() => toggleComp(c)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold border transition-colors duration-300 ${
                    active ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B192C]"
                  }`}>
                  {active && <Check size={13} />} {c}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cp-additional" className={labelCls}>Additional information</label>
          <textarea id="cp-additional" data-testid="cp-input-additional" rows={4}
            value={form.additional} onChange={upd("additional")}
            placeholder="Services offered, EPS volume, consultation room availability, parking, anything that helps us match the right pharmacist..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cp-preferred" className="flex items-start gap-3 cursor-pointer select-none">
            <input id="cp-preferred" type="checkbox" data-testid="cp-preferred-checkbox"
              checked={form.preferred} onChange={(e) => setForm((f) => ({ ...f, preferred: e.target.checked }))}
              className="mt-0.5 w-[18px] h-[18px] accent-[#10B981]" />
            <span className="text-sm text-[#475569]">
              I would also like information about becoming an NCS Preferred Supplier Partner.
            </span>
          </label>
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="cp-web">Website</label>
          <input id="cp-web" data-testid="cp-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={upd("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Please do not include patient information. Submitting a vacancy is free and without
          obligation — availability and commercial terms are always confirmed with you before any
          assignment proceeds. Read our <PrivacyLink testid="cp-vacancy-privacy-link" />.
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="cp-vacancy-submit"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Submit Vacancy to NCS <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* ---------------------------- Partnership form ---------------------------- */
const SupplierForm = () => {
  const empty = {
    organisation: "", contact_name: "", job_title: "", email: "", phone: "",
    branches: "", regions: "", requirements: "", frequency: FREQUENCY[0],
    additional: "", website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const details = [
      { label: "Job title", value: form.job_title },
      ...(form.branches ? [{ label: "Number of pharmacy branches", value: form.branches }] : []),
      ...(form.regions ? [{ label: "Main operating regions", value: form.regions }] : []),
      ...(form.requirements ? [{ label: "Estimated cover requirements", value: form.requirements }] : []),
      { label: "Frequency of locum requirements", value: form.frequency },
    ];
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.contact_name,
        organisation: form.organisation,
        org_type: "Preferred Supplier Enquiry",
        email: form.email,
        phone: form.phone,
        requirement: "Preferred Supplier Partnership enquiry",
        start_date: null,
        additional: form.additional || null,
        journey: "preferred-supplier",
        website: form.website,
        calculator: details,
      });
      setSent(true);
      toast.success("Enquiry submitted — we'll arrange a partnership discussion.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="cp-ps-success" className="bg-white border border-[#10B981]/30 rounded-3xl p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Partnership enquiry received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.contact_name.split(" ")[0]}. We'll be in touch to arrange a partnership
          discussion about {form.organisation}. A confirmation email is on its way to {form.email}.
        </p>
        <button data-testid="cp-ps-new-button" onClick={() => { setSent(false); setForm(empty); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="cp-partnership-form"
      className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="ps-org" className={labelCls}>Organisation / pharmacy group name *</label>
          <input id="ps-org" data-testid="cp-ps-input-organisation" required minLength={2}
            value={form.organisation} onChange={upd("organisation")} placeholder="NorthWest Pharmacies Ltd" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-contact" className={labelCls}>Contact name *</label>
          <input id="ps-contact" data-testid="cp-ps-input-contact-name" required minLength={2}
            value={form.contact_name} onChange={upd("contact_name")} placeholder="Amina Shah" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-job" className={labelCls}>Job title *</label>
          <input id="ps-job" data-testid="cp-ps-input-job-title" required minLength={2}
            value={form.job_title} onChange={upd("job_title")} placeholder="Operations Manager" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-email" className={labelCls}>Email *</label>
          <input id="ps-email" data-testid="cp-ps-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="a.shah@pharmacy.co.uk" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-phone" className={labelCls}>Telephone *</label>
          <input id="ps-phone" data-testid="cp-ps-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={upd("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-branches" className={labelCls}>Number of pharmacy branches</label>
          <input id="ps-branches" data-testid="cp-ps-input-branches" type="number" min="1"
            value={form.branches} onChange={upd("branches")} placeholder="e.g. 6" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-regions" className={labelCls}>Main operating regions</label>
          <input id="ps-regions" data-testid="cp-ps-input-regions"
            value={form.regions} onChange={upd("regions")} placeholder="e.g. Greater Manchester, Merseyside" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ps-requirements" className={labelCls}>Estimated pharmacist cover requirements</label>
          <input id="ps-requirements" data-testid="cp-ps-input-requirements"
            value={form.requirements} onChange={upd("requirements")} placeholder="e.g. 10 locum days per month" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ps-frequency" className={labelCls}>Frequency of locum requirements</label>
          <select id="ps-frequency" data-testid="cp-ps-select-frequency" value={form.frequency} onChange={upd("frequency")} className={inputCls}>
            {FREQUENCY.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ps-additional" className={labelCls}>Additional information</label>
          <textarea id="ps-additional" data-testid="cp-ps-input-additional" rows={4}
            value={form.additional} onChange={upd("additional")}
            placeholder="Current workforce arrangements, typical cover patterns, services offered across branches..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="ps-web">Website</label>
          <input id="ps-web" data-testid="cp-ps-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={upd("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Preferred supplier terms are agreed individually with each organisation. Submitting this
          enquiry does not guarantee pharmacist availability — it starts a conversation about your
          recurring requirements. Read our <PrivacyLink testid="cp-ps-privacy-link" />.
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="cp-ps-submit"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Request a Partnership Discussion <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* --------------------------------- Page --------------------------------- */
const CommunityPharmacy = () => {
  const config = usePublicConfig();
  const [vacancyCoverType, setVacancyCoverType] = useState(null);
  const [vacancyPrefill, setVacancyPrefill] = useState(null);

  const openVacancy = ({ coverType, additional }) => {
    if (coverType) setVacancyCoverType(coverType + "#" + Date.now());
    if (additional) setVacancyPrefill(additional + "#" + Date.now());
    scrollToId("vacancy");
  };
  // strip the cache-busting suffix when passing down
  const coverTypeValue = vacancyCoverType ? vacancyCoverType.split("#")[0] : null;
  const prefillValue = vacancyPrefill ? vacancyPrefill.split("#")[0] : null;

  return (
    <main data-testid="cp-page">
      <Seo
        title="NCS Community Pharmacy | Pharmacist Locum Cover & Workforce Solutions"
        description="Flexible GPhC-registered pharmacist cover for community pharmacies — urgent locum cover, short-term and long-term placements, and independent prescribing pharmacists. Submit your vacancy to NCS."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
              <Store size={14} /> Pharmacist workforce for community pharmacy
            </p>
            <h1 data-testid="cp-hero-heading"
              className="mt-4 font-display font-extrabold tracking-tight leading-[1.06] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>NCS Community Pharmacy</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="font-serif-accent">Reliable pharmacist cover.</span> When you need it most.
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-6 space-y-4 text-base leading-relaxed text-[#475569] max-w-2xl">
              <p>
                National Clinical Services provides flexible pharmacist workforce solutions to community
                pharmacies across the UK.
              </p>
              <p>
                Whether you need urgent last-minute locum cover, short-term support or a longer-term
                pharmacist placement, our network of GPhC-registered pharmacists can help support your
                pharmacy's staffing requirements. Our pharmacists bring experience across community
                pharmacy services, including Pharmacy First, vaccination programmes and clinical service
                delivery. We also have access to independent prescribing pharmacists for roles requiring
                additional clinical expertise, subject to availability and relevant competencies.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 flex flex-wrap gap-3.5">
              <button onClick={() => scrollToId("vacancy")} data-testid="cp-hero-vacancy-btn"
                className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Submit a Pharmacist Vacancy
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button onClick={() => openVacancy({ coverType: "Urgent / Last-Minute" })} data-testid="cp-hero-urgent-btn"
                className="inline-flex items-center gap-2.5 bg-[#EF4444] text-white font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C]">
                <Zap size={16} /> Request Urgent Locum Cover
              </button>
              <button onClick={() => scrollToId("partnership")} data-testid="cp-hero-partner-btn"
                className="inline-flex items-center gap-2.5 text-[#0B192C] font-semibold px-6 py-4 rounded-full text-sm border border-[#0B192C]/25 transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Become a Preferred Supplier Partner
              </button>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative">
              <img src="/assets/ncs-mark.png?v=2" alt="" aria-hidden="true"
                className="absolute -top-12 -left-12 w-40 opacity-20 -rotate-12 select-none pointer-events-none" />
              <div className="relative rounded-3xl overflow-hidden border-[6px] border-white shadow-[0_16px_40px_-12px_rgba(11,25,44,0.12)]">
                <img src="/assets/cp-hero.png" alt="A pharmacist advising a customer at the counter of a modern UK community pharmacy"
                  data-testid="cp-hero-image"
                  className="w-full h-[340px] sm:h-[440px] object-cover" />
              </div>
              <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-sm border border-[#E2E8F0] px-5 py-3.5">
                <p className="font-display text-sm font-bold text-[#0B192C]">GPhC-registered. Verified before every placement.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WORKFORCE SOLUTIONS */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our pharmacist workforce solutions</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl">
              Flexible Pharmacist Cover for Every Pharmacy
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 4) * 0.07}>
                <div data-testid={`cp-solution-card-${i + 1}`}
                  className="h-full flex flex-col bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569] flex-1">{s.text}</p>
                  <button onClick={() => openVacancy({ coverType: s.coverType })} data-testid={`cp-solution-cta-${i + 1}`}
                    className="group mt-5 inline-flex items-center gap-2 text-sm font-bold transition-colors duration-300"
                    style={{ color: s.color }}>
                    Request Pharmacist Cover
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLINICAL SKILLS */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our pharmacists' clinical skills</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              Qualified Pharmacists. Clinical Expertise. Professional Standards.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SKILLS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div data-testid={`cp-skill-card-${i + 1}`}
                  className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <div className="flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-2xl px-6 py-5 max-w-3xl">
              <ShieldCheck size={18} className="text-[#10B981] shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed text-[#475569]">
                Individual qualifications, training, current competence and service eligibility are
                verified for each pharmacist and every assignment. Not every pharmacist in the NCS
                network holds an independent prescribing qualification — prescribing roles are matched
                only to appropriately qualified and competent pharmacists.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-20 sm:py-28 lg:py-32 bg-[#0B192C] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#C5A059]">How NCS works</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
              From Vacancy to Pharmacist Cover
            </h2>
            <p data-testid="cp-flow-label"
              className="mt-5 inline-flex flex-wrap justify-center items-center gap-x-2 gap-y-1 text-xs sm:text-sm font-semibold text-white/60">
              Submit Vacancy <ArrowRight size={13} className="text-[#06B6D4]" />
              Requirements Review <ArrowRight size={13} className="text-[#06B6D4]" />
              Pharmacist Sourcing <ArrowRight size={13} className="text-[#06B6D4]" />
              Confirmation <ArrowRight size={13} className="text-[#06B6D4]" />
              Pharmacist Cover
            </p>
          </Reveal>
          <div className="mt-14 flex flex-col lg:flex-row items-stretch justify-center gap-4 lg:gap-2">
            {STEPS.map((s, i) => (
              <Fragment key={s.title}>
                <Reveal delay={i * 0.1} className="flex-1">
                  <div className="w-full h-full flex flex-col bg-white/[0.06] border border-white/10 rounded-3xl p-6 transition-colors duration-300 hover:bg-white/[0.10]"
                    data-testid={`cp-step-${i + 1}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-[#C5A059] text-[#0B192C] font-display font-extrabold text-sm flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <span className="w-11 h-11 rounded-2xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center shrink-0">
                        <s.icon size={20} />
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-base font-bold text-white leading-snug uppercase tracking-wide">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-white/60 flex-1">{s.text}</p>
                  </div>
                </Reveal>
                {i < STEPS.length - 1 && (
                  <>
                    <ArrowRight size={18} className="hidden lg:block text-[#10B981] self-center shrink-0" aria-hidden="true" />
                    <ArrowDown size={20} className="lg:hidden text-[#10B981] self-center shrink-0" aria-hidden="true" />
                  </>
                )}
              </Fragment>
            ))}
          </div>
          <Reveal className="text-center mt-12">
            <button onClick={() => scrollToId("vacancy")} data-testid="cp-process-cta"
              className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
              Submit Your Vacancy Today
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* SOURCING FEES */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Transparent sourcing fees</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl">
              Simple, Transparent Pharmacist Sourcing
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569] max-w-3xl">
              At National Clinical Services, we believe pharmacy owners should have access to a
              straightforward and transparent pharmacist sourcing service. We operate a fixed sourcing
              fee model for agreed pharmacist placements, helping pharmacy businesses understand their
              recruitment costs before confirming an assignment.
            </p>
          </Reveal>
          {config.pharmacy_sourcing_fee_public && (
            <Reveal className="mt-8">
              <div data-testid="cp-fee-public"
                className="inline-flex items-center gap-3 bg-[#F0FDF4] border border-[#DCFCE7] rounded-2xl px-6 py-4">
                <PoundSterling size={18} className="text-[#10B981]" />
                <p className="text-sm font-bold text-[#0B192C]">{config.pharmacy_sourcing_fee_public}</p>
              </div>
            </Reveal>
          )}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {FEE_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.08}>
                <div data-testid={`cp-fee-card-${i + 1}`}
                  className="h-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${c.color}1A`, color: c.color }}>
                    <c.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <div className="flex flex-wrap items-center gap-6">
              <button
                onClick={() => openVacancy({ additional: "Please send me the NCS sourcing fee schedule." })}
                data-testid="cp-fee-schedule-btn"
                className="group inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] hover:text-[#0B192C]">
                Request Our Sourcing Fee Schedule
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-xs leading-relaxed text-[#64748B] max-w-md">
                The NCS sourcing fee is agreed with your pharmacy in advance and is separate from the
                pharmacist's own locum or employment charges, which are arranged between the pharmacy
                and the pharmacist.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VACANCY FORM */}
      <section id="vacancy" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EF4444]">Pharmacist vacancies</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Submit Your Pharmacist Vacancy
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Looking for pharmacist cover? Complete the form below and our team will review your
              requirements.
            </p>
          </Reveal>
          <Reveal>
            <VacancyForm key={`${coverTypeValue}|${prefillValue}`} coverType={coverTypeValue} prefillAdditional={prefillValue} />
          </Reveal>
        </div>
      </section>

      {/* PREFERRED SUPPLIER */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Preferred supplier partnerships</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Make NCS Your Preferred Pharmacist Workforce Partner
            </h2>
            <p className="mt-3 text-lg font-semibold text-[#0B192C]">
              One trusted contact for your pharmacy staffing requirements.
            </p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#475569]">
              <p>
                National Clinical Services welcomes preferred supplier relationships with independent
                pharmacies, pharmacy groups and multi-branch operators.
              </p>
              <p>
                Rather than sourcing pharmacists individually whenever a staffing gap arises, your
                organisation can establish an ongoing working relationship with NCS. Our team can become
                a dedicated point of contact for pharmacist staffing enquiries, helping coordinate
                requirements across one or multiple pharmacy branches.
              </p>
            </div>
            <button onClick={() => scrollToId("partnership")} data-testid="cp-partnership-cta"
              className="group mt-7 inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
              Become an NCS Preferred Supplier Partner
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
              <img src="/assets/cp-partner.png" alt="Two pharmacy professionals coordinating stock in a modern community pharmacy aisle"
                data-testid="cp-partner-image" loading="lazy"
                className="w-full h-[280px] sm:h-[360px] object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PARTNER_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 5) * 0.07}>
                <div data-testid={`cp-partner-card-${i + 1}`}
                  className="h-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${b.color}1A`, color: b.color }}>
                    <b.icon size={20} />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold text-[#0B192C] leading-snug">{b.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-[#475569]">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP FORM */}
      <section id="partnership" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">For pharmacy groups & multi-branch operators</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Discuss a Preferred Supplier Agreement
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Tell us about your organisation and recurring pharmacist requirements — we'll arrange a
              partnership discussion.
            </p>
          </Reveal>
          <Reveal>
            <SupplierForm />
          </Reveal>
          <Reveal className="mt-8 text-center">
            <p className="inline-flex items-center gap-2 text-xs text-[#64748B]">
              <Mail size={13} className="text-[#06B6D4]" />
              Prefer email? Write to us at yasin@nationalclinicalservices.co.uk
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CommunityPharmacy;
